import React, { useState, useEffect } from "react";
import { Select, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  BankOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { getData } from "../../config/api-connect";
import { getColumnSearchProps, getColumnFilters } from "../../utils/filters";
import SummaryLayout from "../../components/layout/SummaryLayout";
import MonthSelector from "../../components/ui/MonthSelector";
import { useMonthFilter } from "../../hooks/useMonthFilter";

const { Option } = Select;

function GstInvoices() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const passedVenueId = location.state?.venue_id;

  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [venues, setVenues] = useState([]);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    getData("/finance/get_cities_and_venues")
      .then((response) => {
        const allCities = response.cities || [];
        const allVenues = response.venues || [];
        setCities(allCities);
        setVenues(allVenues);

        if (passedVenueId) {
          const venueObj = allVenues.find((v) => v.id === passedVenueId);
          if (venueObj) {
            const filtered = allVenues.filter(
              (item) => item.city === venueObj.city,
            );
            setVenuelist(filtered);
            setSelectedCityId(venueObj.city);
            setSelectedVenueId(passedVenueId);
          }
        } else {
          setVenuelist(allVenues);
          setSelectedCityId("");
          setSelectedVenueId("");
        }
        setInitialized(true);
      })
      .catch((error) => console.error("Error:", error.message));
  }, [passedVenueId]);

  useEffect(() => {
    if (selectedCityId) {
      setVenuelist(venues.filter((item) => item.city === selectedCityId));
      const currentVenue = venues.find((v) => v.id === selectedVenueId);
      if (currentVenue && currentVenue.city !== selectedCityId) {
        setSelectedVenueId("");
      }
    } else {
      setVenuelist(venues);
    }
  }, [selectedCityId, venues]);

  useEffect(() => {
    if (!initialized) return;
    getGstInvoices();
  }, [initialized, selectedCityId, selectedVenueId, selectedMonth]);

  const getGstInvoices = () => {
    setLoading(true);

    const month = selectedMonth.format("MM");
    const year = selectedMonth.format("YYYY");
    const cityQuery = selectedCityId ? `&city_id=${selectedCityId}` : "";
    const venueQuery = selectedVenueId ? `&venue_id=${selectedVenueId}` : "";

    getData(
      `/finance/get_gst_invoices/?month=${month}&year=${year}${cityQuery}${venueQuery}`,
    )
      .then((response) => {
        setInvoices(response.result || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching invoices:", error.message);
      });
  };

  const uniqueValues = (key) =>
    Array.from(new Set(invoices.map((r) => r[key]).filter(Boolean))).sort();

  const columns = [
    {
      title: "Invoice Number",
      dataIndex: "invoice_number",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      ...getColumnSearchProps("invoice_number"),
      onCell: () => ({ style: { whiteSpace: "nowrap" } }),
    },
    {
      title: "Customer",
      dataIndex: "customer",
      align: "left",
      ...getColumnSearchProps("customer"),
      onFilter: (value, record) => {
        const name = record?.customer
          ? record.customer.toString().toLowerCase()
          : "";
        const mobile = record?.mobile_no
          ? record.mobile_no.toString().toLowerCase()
          : "";
        return (
          name.includes(value.toLowerCase()) ||
          mobile.includes(value.toLowerCase())
        );
      },
      render: (text, record) => (
        <>
          <div>{text ?? <span style={{ color: "#aaa" }}>—</span>}</div>
          <div style={{ color: "dimgrey", marginTop: 4 }}>
            {record?.mobile_no ?? ""}
          </div>
        </>
      ),
    },
    {
      title: "Venue",
      dataIndex: "venue",
      align: "center",
      ...getColumnFilters("venue", uniqueValues("venue")),
    },
    {
      title: "Package",
      dataIndex: "package",
      align: "left",
      width: 250,
      ...getColumnSearchProps("package"),
    },
    {
      title: "Sales Model",
      dataIndex: "sales_model",
      align: "center",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      ...getColumnFilters("sales_model", uniqueValues("sales_model")),
    },
    {
      title: "Slab",
      dataIndex: "slab",
      align: "center",
      ...getColumnFilters("slab", uniqueValues("slab")),
    },
    {
      title: "Taxable Value",
      dataIndex: "taxable_value",
      align: "right",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      sorter: (a, b) =>
        (Number(a.taxable_value) || 0) - (Number(b.taxable_value) || 0),
      render: (val) =>
        val == null ? "—" : Number(val).toLocaleString("en-IN"),
    },
    {
      title: "CGST",
      dataIndex: "cgst",
      align: "right",
      render: (val) =>
        val == null ? "—" : Number(val).toLocaleString("en-IN"),
    },
    {
      title: "SGST",
      dataIndex: "sgst",
      align: "right",
      render: (val) =>
        val == null ? "—" : Number(val).toLocaleString("en-IN"),
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "right",
      onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0),
      render: (val) =>
        val == null ? (
          <span style={{ color: "#aaa" }}>—</span>
        ) : (
          <strong>{Number(val).toLocaleString("en-IN")}</strong>
        ),
    },
    {
      title: "",
      dataIndex: "pdf",
      align: "center",
      fixed: "right",
      render: (pdf) => {
        return (
          <Tooltip
            title={pdf ? "View Invoice PDF" : "Invoice PDF not available"}
          >
            <EyeOutlined
              style={{
                fontSize: "18px",
                color: pdf ? "#1890ff" : "#ccc",
                cursor: pdf ? "pointer" : "not-allowed",
              }}
              onClick={(e) => {
                if (!pdf) return;
                e.stopPropagation();
                window.open(pdf, "_blank", "noopener,noreferrer");
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const pageTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <ArrowLeftOutlined
        style={{ cursor: "pointer", fontSize: "20px" }}
        onClick={() => navigate(-1)}
      />
      <span>GST Invoices</span>
    </div>
  );

  const chooseCity = (value) => {
    setSelectedCityId(value);
    setSelectedVenueId("");
    localStorage.setItem("selectedCity", JSON.stringify(value));
  };

  const renderRightSideActions = () => (
    <>
      <Select
        showSearch
        placeholder="Select City"
        style={{ width: 170, marginRight: 8 }}
        value={selectedCityId}
        onChange={(value) => chooseCity(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="">All Cities</Option>
        {cities.map((city) => (
          <Option key={city.id} value={city.id}>
            {city.name}
          </Option>
        ))}
      </Select>

      <Select
        showSearch
        placeholder="Select Venue"
        style={{ width: 250, marginRight: 8 }}
        value={selectedVenueId}
        onChange={(value) => setSelectedVenueId(value)}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        <Option value="">All Venues</Option>
        {venuelist.map((venue) => (
          <Option key={venue.id} value={venue.id}>
            {venue.name}
          </Option>
        ))}
      </Select>

      <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
    </>
  );

  const totalTaxable = invoices.reduce(
    (acc, curr) => acc + (Number(curr.taxable_value) || 0),
    0,
  );
  const totalGst = invoices.reduce(
    (acc, curr) => acc + (Number(curr.cgst) || 0) + (Number(curr.sgst) || 0),
    0,
  );
  const totalAmount = invoices.reduce(
    (acc, curr) => acc + (Number(curr.total) || 0),
    0,
  );

  const statsConfig = [
    {
      key: "s_invoices",
      title: "Total Invoices",
      icon: <FileTextOutlined style={{ fontSize: 32, color: "#1890ff" }} />,
    },
    {
      key: "s_taxable",
      title: "Total Taxable Value",
      icon: <DatabaseOutlined style={{ fontSize: 32, color: "#1565c0" }} />,
    },
    {
      key: "s_gst",
      title: "Total GST Collected",
      icon: (
        <SafetyCertificateOutlined style={{ fontSize: 32, color: "#faad14" }} />
      ),
    },
    {
      key: "s_total",
      title: "Total Amount",
      icon: <BankOutlined style={{ fontSize: 32, color: "#52c41a" }} />,
    },
  ];

  const totals = {
    s_invoices: invoices.length,
    s_taxable: `₹ ${totalTaxable.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
    s_gst: `₹ ${totalGst.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
    s_total: `₹ ${totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
  };

  return (
    <>
      <SummaryLayout
        title={pageTitle}
        headerContent={renderRightSideActions()}
        statsConfig={statsConfig}
        totals={totals}
        statsLoading={loading}
        tableProps={{
          rowKey: "id",
          columns,
          data: invoices,
          loading: loading,
          pagination: { pageSize: 20, showSizeChanger: false },
          onRow: (record) => ({
            style: { cursor: "pointer" },
          }),
        }}
      />
    </>
  );
}

export default GstInvoices;
